import { useCallback, useEffect, useRef, useState } from 'react';
import elasticsearchApi from 'services/elasticsearchApi';
import { Params, useSearch } from './search';

const TWITTER_SERVICE = ['twitter'];

type TwitterCounts = Map<string, number>;

export default function useTwitterPostsCounts(): {
  counts: TwitterCounts;
  loading: boolean;
} {
  const { queryBuilder, params, searchedTerms, comparisonTerms } = useSearch();

  const [counts, setCounts] = useState<TwitterCounts>(new Map());
  const [loading, setLoading] = useState(false);

  const requestsInFlight = useRef(0);

  const finalize = useCallback(() => {
    requestsInFlight.current = Math.max(0, requestsInFlight.current - 1);
    if (requestsInFlight.current === 0) {
      setLoading(false);
    }
  }, []);

  const fetchCount = useCallback(
    async (target: Params) => {
      if (!target?.obligatory) {
        return;
      }

      const hasFilters =
        target.obligatory !== '' ||
        target.optional !== '' ||
        target.banned !== '';

      if (!hasFilters) {
        return;
      }

      requestsInFlight.current += 1;
      setLoading(true);

      try {
        const response = await elasticsearchApi.post<{ count: number }>(
          '/historical_posts.json',
          queryBuilder({
            service: TWITTER_SERVICE,
            size: 1,
            sort_by: 'top_posts_field',
            customParams: target,
          }),
        );

        const count = response.data?.count ?? 0;
        setCounts(prev => {
          const next = new Map(prev);
          next.set(target.obligatory, count);
          return next;
        });
      } catch {
        setCounts(prev => {
          const next = new Map(prev);
          next.set(target.obligatory, 0);
          return next;
        });
      } finally {
        finalize();
      }
    },
    [finalize, queryBuilder],
  );

  useEffect(() => {
    fetchCount(params);
  }, [fetchCount, params]);

  useEffect(() => {
    comparisonTerms.forEach(fetchCount);
  }, [comparisonTerms, fetchCount]);

  useEffect(() => {
    const activeKeys = searchedTerms.map(term => term.obligatory);
    setCounts(prev => {
      const next = new Map(prev);

      Array.from(next.keys()).forEach(key => {
        if (!activeKeys.includes(key)) {
          next.delete(key);
        }
      });

      return next;
    });
  }, [searchedTerms]);

  return { counts, loading };
}
